import { useMemo } from 'preact/hooks'
import useDetectBreakpoint from 'hooks/useDetectBreakpoint'
import useBundleGlobal from 'hooks/useBundleGlobal'
import { groupParamsByKey } from 'libs/search-native/search-native-utils'
import clsx from 'clsx'
import { isLoading, isFiltering, products } from 'snippets/plp-native/@store'
import {
  PlpNativeSkeletonCard,
  PlpNativeSkeletonGrid,
  PlpNativeSkeletonGridArticle,
  PlpNativeSkeletonGridPage
} from 'snippets/plp-native/plp-native-skeleton/plp-native-skeleton-grid'
import ProductCard from 'snippets/product-card/product-card'
import PlpBannerCard from 'snippets/plp-native/plp-banner-card'
import ArticleCard from 'snippets/article/article-card'
import PageCard from 'snippets/page/page-card'

function PlpNativeGrid({ perPage, filterType, countFilterSelected, onClickProduct }) {
  const productCount = products.value?.filter((item) => item.type !== 'banner')?.length
  const { isDesktop } = useDetectBreakpoint()
  const { productBundles } = useBundleGlobal()
  const lazyLoadIndex = !isDesktop ? 4 : 8

  if (isFiltering.value) {
    const SkeletonMap = {
      article: PlpNativeSkeletonGridArticle,
      page: PlpNativeSkeletonGridPage,
      product: PlpNativeSkeletonGrid
    }
    const Comp = SkeletonMap[filterType] ?? PlpNativeSkeletonGrid

    return <Comp number={perPage} />
  }

  const emptyText = useMemo(() => {
    const { href, pathname } = window.location

    const paramsString = href.split('?')[1]
    const isSearchPage = pathname === '/search'
    const paramsDefault = groupParamsByKey(new URLSearchParams(paramsString))

    if (isSearchPage) {
      return (
        <div className="container h-[250px] flex items-center justify-center lg:h-[400px]">
          {translate.collection.noResults.replace('{{ terms }}', paramsDefault.searchQuery)}
        </div>
      )
    }

    return (
      <div className="container mx-auto mb-10 mt-6 text-center md:mt-10 md:max-w-md">
        <h2 className="text-lg capitalize text-neutral-900">{translate.collection.titleNoProducts}</h2>
        <p className="mt-4 text-xs text-neutral-900 lg:text-base">{translate.collection.contentNoProducts}</p>
      </div>
    )
  }, [])

  if ((!isFiltering.value || !isLoading.value) && !productCount) {
    if (isFiltering.value || isLoading.value) {
      return <PlpNativeSkeletonGrid number={perPage} />
    }

    return emptyText
  }

  return (
    <div
      className={clsx('mb-6 grid grid-cols-2 -mr-px lg:mb-10 lg:grid-cols-4 relative after:absolute after:bottom-0 after:left-0 after:w-full after:h-px after:bg-neutral-900', {
        '!grid-cols-1 lg:!grid-cols-3': filterType === 'article',
        '!grid-cols-1 px-5 pt-5 pb-8 lg:px-10 lg:pt-8 lg:pb-10': filterType === 'page'
      })}
    >
      {products.value.map((product, index) => {
        if (product?.type === 'banner') {
          return <PlpBannerCard key={product.position} product={product} countFilterSelected={countFilterSelected} />
        }

        if (product?.type === 'article') {
          return <ArticleCard key={`article-${product.id}`} article={product} />
        }

        if (product?.type === 'page') {
          return <PageCard key={`page-${product.id}`} page={product} />
        }

        return (
          <ProductCard
            key={product.id}
            product={product}
            productBundles={productBundles}
            isLazy={index >= lazyLoadIndex}
            isDesktop={isDesktop}
            classProductWrap="flex-col border-r border-b border-neutral-900"
            classProductInfo=""
            useCustomButtonAtc
            onClickProduct={onClickProduct}
            scrollImageMobile
          />
        )
      })}
      {isLoading.value && <PlpNativeSkeletonCard number={perPage} />}
    </div>
  )
}

export default PlpNativeGrid
